import React from "react";
import { MdPerson } from "react-icons/md";
import { ProgressBar } from "react-bootstrap";

interface Student {
  name?: string;
  student_name?: string;
  student_id?: string;
  progress?: number;
}

interface CategoryData {
  count: number;
  percentage: number;
  students: Student[];
}

interface LearningProgressData {
  current_progress: number;
  phases: string[];
  ahead: CategoryData;
  behind: CategoryData;
  on_track: CategoryData;
  total_students: number;
}

interface LearningProgressProps {
  learningProgressData: LearningProgressData | null;
  loading: boolean;
  onShowMore: (category: string) => void;
}

const LearningProgress: React.FC<LearningProgressProps> = ({
  learningProgressData,
  loading,
  onShowMore,
}) => {
  const renderStudentList = (
    students: Student[],
    maxDisplay: number = 10,
    showProgress: boolean = false
  ) => {
    if (!students || students.length === 0) {
      return <div className="d-flex flex-column gap-1"></div>;
    }

    const displayStudents = students.slice(0, maxDisplay);

    return (
      <div className="d-flex flex-column gap-1 flex-grow-1">
        {displayStudents.map((student, index) => (
          <div
            key={student.student_id || index}
            className="d-flex align-items-center gap-2 overflow-hidden"
            style={{ fontSize: "11px", color: "#666" }}
          >
            <MdPerson style={{ width: "20px", height: "20px", color: "#6c757d" }} />
            <span className="flex-grow-1 text-truncate">{student.name || student.student_name}</span>
            {showProgress && student.progress !== undefined && (
              <span style={{ fontSize: "11px", color: "#666", fontWeight: "500" }}>
                {student.progress.toFixed(1)}%
              </span>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="d-flex flex-column" style={{ flex: 1, minHeight: 0 }}>
      {/* Header */}

      {/* Phases Display */}
      { learningProgressData && learningProgressData?.phases?.length && learningProgressData?.phases?.length > 0 && (
  <div
    className="d-flex align-items-stretch w-100 rounded-3 py-2 my-2"
    style={{ backgroundColor: "#f5f5f5", boxShadow: "0 2px 4px rgba(0,0,0,0.2)" }}
  >
    {learningProgressData.phases.map((phase, index) => (
      <div
        key={index}
        title={phase}
        className={`d-flex align-items-center justify-content-center text-center px-2 small fw-medium text-secondary
          ${index !== learningProgressData.phases.length - 1 ? "border-end" : ""}
        `}
        style={{
          flex: "1 1 0",
          minWidth: 0,
          wordBreak: "break-word",
          overflowWrap: "anywhere",
          lineHeight: 1.25,
        }}
      >
        {phase}
      </div>
    ))}
  </div>
)}


      {/* Current Course Progress Bar */}
      {learningProgressData?.current_progress !== undefined && (
        <div className="position-relative">
          <ProgressBar
            className="w-100"
            style={{
              height: "20px",
              borderRadius: "10px",
              backgroundColor: "#81C784",
              display: "flex",
              alignItems: "stretch",
            }}
          >
            <ProgressBar
              now={learningProgressData.current_progress}
              style={{
                backgroundColor: "#52872a",
                height: "100%",
                minHeight: "20px",
                alignSelf: "stretch",
              }}
            />
          </ProgressBar>
          <div
            className="position-absolute d-flex align-items-center justify-content-center w-100 text-white"
            style={{
              top: 0,
              left: 0,
              height: "20px",
              pointerEvents: "none",
            }}
          >
            Current Course Progress: {learningProgressData.current_progress}%
          </div>
        </div>
      )}

      {/* Student Categories */}
      <div className="d-flex flex-nowrap gap-3 mt-2" style={{ flex: 1, minHeight: 0, alignItems: "stretch" }}>
        <div className="d-flex flex-column gap-2 flex-fill" style={{ minWidth: 0, minHeight: 0 }}>
          <h5 className="mb-0" style={{ fontSize: "12px", fontWeight: 600, color: "#333" }}>
            Ahead [{learningProgressData?.ahead.count || 0}]{" "}
            {learningProgressData?.ahead.percentage?.toFixed(1) || 0}%
          </h5>
          <div style={{ flex: 1, minHeight: 0 }}>
            {loading ? (
              <div>Loading students...</div>
            ) : (
              renderStudentList(learningProgressData?.ahead.students || [], 10, false)
            )}
          </div>
          <button
            className="btn btn-outline-primary btn-sm w-100 mt-auto"
            onClick={() => onShowMore("ahead")}
            disabled={
              !learningProgressData?.ahead.students ||
              learningProgressData.ahead.students.length === 0
            }
          >
            Show More
          </button>
        </div>

        <div className="d-flex flex-column gap-2 flex-fill" style={{ minWidth: 0, minHeight: 0 }}>
          <h5 className="mb-0" style={{ fontSize: "12px", fontWeight: 600, color: "#333" }}>
            On-time [{learningProgressData?.on_track.count || 0}]{" "}
            {learningProgressData?.on_track.percentage?.toFixed(1) || 0}%
          </h5>
          <div style={{ flex: 1, minHeight: 0 }}>
            {loading ? (
              <div>Loading students...</div>
            ) : (
              renderStudentList(learningProgressData?.on_track.students || [], 10, false)
            )}
          </div>
          <button
            className="btn btn-outline-primary btn-sm w-100 mt-auto"
            onClick={() => onShowMore("on_track")}
            disabled={
              !learningProgressData?.on_track.students ||
              learningProgressData.on_track.students.length === 0
            }
          >
            Show More
          </button>
        </div>

        <div className="d-flex flex-column gap-2 flex-fill" style={{ minWidth: 0, minHeight: 0 }}>
          <h5 className="mb-0" style={{ fontSize: "12px", fontWeight: 600, color: "#333" }}>
            Laggards [{learningProgressData?.behind.count || 0}]{" "}
            {learningProgressData?.behind.percentage?.toFixed(1) || 0}%
          </h5>
          <div style={{ flex: 1, minHeight: 0 }}>
            {loading ? (
              <div>Loading students...</div>
            ) : (
              renderStudentList(learningProgressData?.behind.students || [], 10, true)
            )}
          </div>
          <button
            className="btn btn-outline-primary btn-sm w-100 mt-auto"
            onClick={() => onShowMore("behind")}
            disabled={
              !learningProgressData?.behind.students ||
              learningProgressData.behind.students.length === 0
            }
          >
            Show More
          </button>
        </div>
      </div>
    </div>
  );
};

export default LearningProgress;
